import Cookies from 'js-cookie';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useLang } from '../language/LanguageProvider.jsx';
import { useAuth } from '../other/auth';
import { media } from '../other/breakpoints';
import { useModalClose } from '../other/use-modal-close';
import { getLogo } from '../utils/logo';
import Popup from './Popup/Popup';

const Container = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: var(--first-color);
  transition: all 0.5s ease;
  overflow: hidden;

  & > * > input {
    background-color: var(--first-color);
    color: white;
  }
`;

const Box = styled.form`
  width: 100%;
  max-width: 500px;
  left: 50%;
  top: 30%;
  transform: translate(-50%, -50%);
  position: absolute;
  box-sizing: border-box;
  padding: 0 2rem;

  ${media.tablet} {
    width: 80vw;
    max-width: 500px;
    padding: 0 2rem;
    top: 45%;
  }

  ${media.mobile} {
    width: 90vw;
    max-width: none;
    padding: 0 1.5rem;
    top: 45%;
  }

  & > h2 {
    color: white;
    text-align: center;
    font-size: 2rem;
    margin-bottom: 2rem;
    margin-top: 0;

    ${media.tablet} {
      font-size: 1.75rem;
      margin-bottom: 1.75rem;
    }

    ${media.mobile} {
      font-size: 1.5rem;
      margin-bottom: 1.5rem;
    }

    ${media.mobileMenu} {
      font-size: 2rem;
      margin-bottom: 4rem;
    }
  }

  & > h5 {
    color: white;
    text-align: center;
    font-size: 1.25rem;
    margin-top: 0;
    margin-bottom: 1.5rem;

    ${media.tablet} {
      font-size: 1.125rem;
      margin-bottom: 1.25rem;
    }

    ${media.mobile} {
      font-size: 1rem;
      margin-bottom: 1rem;
    }

    ${media.mobileMenu} {
      font-size: 1.5rem;
      margin-bottom: 2rem;
    }
  }

  & > label {
    color: white;
    font-size: 1rem;

    ${media.tablet} {
      font-size: 1.125rem;
    }

    ${media.mobile} {
      font-size: 1rem;
    }

    ${media.mobileMenu} {
      font-size: 1.25rem;
    }
  }

  & > h2 > img {
    max-width: 300px;
    max-height: 120px;
    width: auto;
    height: auto;

    ${media.tablet} {
      max-width: 250px;
      max-height: 100px;
    }

    ${media.mobile} {
      max-width: 200px;
      max-height: 80px;
    }

    ${media.mobileMenu} {
      max-width: 350px;
      max-height: 140px;
    }
  }
`;

const Input = styled.input`
  margin-bottom: 1rem;
  box-sizing: border-box;

  ${media.tablet} {
    font-size: 1.125rem;
  }

  ${media.mobile} {
    font-size: 1rem;
    margin-bottom: 0.875rem;
  }

  ${media.mobileMenu} {
    font-size: 1.25rem;
  }

  &:focus {
    color: white;
    border-color: var(--second-color);
    box-shadow: 0 0 0 0.2rem var(--second-color-shadow);
    background-color: var(--first-color);
    transition: all 0.5s ease;
  }
`;

const Button = styled.button`
  color: #fff;
  background-color: var(--second-color);
  transition: all 0.5s ease;
  width: 100%;
  box-sizing: border-box;

  ${media.tablet} {
    font-size: 1.125rem;
  }

  ${media.mobile} {
    font-size: 1rem;
  }

  ${media.mobileMenu} {
    font-size: 1.25rem;
  }

  &:hover,
  &:focus {
    background-color: #829196;
    color: white;
    border-color: #829196;
    box-shadow: 0 0 0 0.2rem #82919608;
    transition: all 0.5s ease;
  }
`;

const Login = ({ url }) => {
  const [dns, setDns] = useState(window.dns || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [blur, setBlur] = useState();
  const [m3u8, setM3u8] = useState(
    window.m3u8warning === true && !Cookies.get('m3u8_play')
  );

  const history = useHistory();
  const auth = useAuth();
  const { t } = useLang();

  useEffect(() => {
    // Se ainda está verificando, aguardar
    if (auth.isChecking && typeof auth.isChecking === 'function' && auth.isChecking()) {
      return;
    }

    const isAuthenticated = auth.isAuth();

    if (isAuthenticated) {
      // Só redirecionar se não estiver já na rota correta
      const targetUrl = url || '/';
      const currentPath = history.location.pathname;

      if (currentPath !== targetUrl && currentPath === '/login/') {
        history.push(targetUrl);
      }
    } else {
      // Tentar login automático apenas UMA VEZ (evitar loop infinito)
      // Mas só se a verificação inicial não encontrou credenciais
      const hasTriedAuth = sessionStorage.getItem('auth_login_tried');

      if (!hasTriedAuth) {
        sessionStorage.setItem('auth_login_tried', 'true');
        // Limpar flag após 5 segundos para permitir nova tentativa se necessário
        setTimeout(() => sessionStorage.removeItem('auth_login_tried'), 5000);

        auth.authLogin(() => {
          // Callback só executa se login for bem-sucedido
          const currentPath = history.location.pathname;
          if (currentPath === '/login/') {
            history.push(url || '/');
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuth(), auth.isChecking]); // Incluir isChecking na dependência

  useEffect(() => {
    m3u8 ? setBlur({ filter: 'blur(.3rem)' }) : setBlur({});
  }, [m3u8]);

  const inputRef = useRef(0);

  const remoteController = (event) => {
    let active = document.activeElement;
    if (event.keyCode === 40 && active.nextSibling) {
      if (active.nextSibling.tagName === 'LABEL') active = active.nextSibling;
      active.nextSibling.focus();
    } else if (event.keyCode === 38 && active.previousSibling) {
      if (active.previousSibling.tagName === 'LABEL')
        active = active.previousSibling;
      active.previousSibling.focus();
    } else if (event.keyCode === 13) active.click();
  };

  const login = (e) => {
    e.preventDefault();
    setBlur({ filter: 'blur(.3rem)' });
    auth.signin(
      dns || window.dns || '',
      username,
      password,
      () => history.replace('/'),
      (title, description) => {
        setShowPopup({ title: title, description: description });
      }
    );
  };

  const closePopup = () => {
    setBlur({ filter: 'blur(0)' });
    setShowPopup(false);
    inputRef.current.focus();
  };

  useModalClose(closePopup, !!showPopup);

  return (
    <>
      <Container style={blur}>
        <Box onKeyDown={remoteController} onSubmit={login}>
          {false && <h2>Welcome to {window.playername}</h2>}
          <h2 className="">
            <img src={getLogo()} alt="" className="d-inline-block" />
          </h2>

          <h5>{t('login.welcome')}</h5>
          <br />
          {!window.dns && [
            <label key="dns-label">{t('login.providerUrl')}</label>,
            <Input
              key="dns-input"
              ref={inputRef}
              className="form-control"
              type="text"
              spellCheck={false}
              placeholder={t('login.providerUrlPlaceholder')}
              name="dns"
              autoFocus
              onChange={(e) => setDns(e.target.value)}
              value={dns}
            />,
          ]}
          <label>{t('login.username')}</label>
          <Input
            ref={window.dns ? inputRef : null}
            className="form-control"
            type="text"
            spellCheck={false}
            placeholder={t('login.username')}
            name="username"
            autoFocus
            onChange={(e) => setUsername(e.target.value)}
            value={username}
          />
          <label>{t('login.password')}</label>
          <Input
            className="form-control"
            type="password"
            spellCheck={false}
            placeholder="****"
            name="password"
            onChange={(e) => setPassword(e.target.value)}
            value={password}
          />
          <Button type="submit" value="1" className="btn">
            {t('login.login')}
          </Button>
        </Box>
      </Container>
      {showPopup && (
        <Popup
          unsecure={true}
          title={showPopup.title}
          description={showPopup.description}
          icon={'fas fa-user-times'}
          onclick={closePopup}
        />
      )}
      {m3u8 && (
        <Popup
          unsecure={true}
          error={false}
          title={t('login.infoTitle')}
          description={t('login.infoDescription', {
            panelName: window.playername || 'IPTVEditor',
          })}
          icon={'fas fa-info-circle'}
          onclick={() => {
            Cookies.set('m3u8_play', 1, { expires: 365 });
            setM3u8(!m3u8);
          }}
        />
      )}
      {/*showKeyboard !== false && isMag && (<KeyboardOverlay onValueChange={onChange} onKeyboardClose={onKeyboardClose} itemValue={showKeyboard}/>)*/}
    </>
  );
};

export default Login;
